import { DateString, toDateString } from '@nextforce/api'
import { ScreenWidth } from '@rneui/base'
import { Icon, makeStyles, useTheme } from '@rneui/themed'
import moment, { Moment } from 'moment'
import React, { useMemo } from 'react'
import { Modal, View } from 'react-native'
import { CalendarList, CalendarListProps } from 'react-native-calendars'
import { SafeAreaProvider, SafeAreaView } from 'react-native-safe-area-context'
import { useThemeChange } from 'settings/change_theme'
import { MarkedDates } from '../schedule/week'

interface Props {
  calendarVisible: boolean
  setCalendarVisible: (calendarVisible: boolean) => void
  markedDates: MarkedDates
  startDate: DateString
  hasNextWeek?: boolean
  hasPreviousWeek?: boolean
  fetchingNextWeek?: boolean
  fetchingPreviousWeek?: boolean
  onCalendarDateSelected(date: Date): void
  onPreviousWeek?(): void
  onNextWeek?(): void
}

export default function CalendarModal({
  calendarVisible,
  setCalendarVisible,
  markedDates,
  onCalendarDateSelected,
  startDate,
}: Props) {
  const { theme } = useTheme()
  const darkMode = useThemeChange().dark
  const styles = useStyles(darkMode)
  const markedCalendar: CalendarListProps['markedDates'] = useMemo(() => {
    let markedWeekDates = Object.assign(
      {},
      ...markedDates.map(i => ({
        [i.date]: {
          marked: true,
          color: 'pink',
          startingDay: false,
          endingDay: false,
          textColor: 'white',
        },
      }))
    )
    if (markedWeekDates === undefined) {
      markedWeekDates = {}
    }
    const today = moment().format('YYYY-MM-DD')
    const endDate = toDateString(moment(startDate).add(6, 'days'))
    for (
      let date: Moment = moment(startDate);
      date.diff(moment(endDate)) <= 0;
      date.add(1, 'day')
    ) {
      const weekIterate = toDateString(date)
      markedWeekDates[weekIterate] = {
        ...markedWeekDates[weekIterate],

        color: darkMode ? '#ff748c' : 'pink',
        textColor: theme.colors?.white,
        selected: false,
      }
    }
    markedWeekDates[startDate] = {
      ...markedWeekDates[startDate],
      startingDay: true,
      endingDay: false,
    }
    markedWeekDates[endDate] = {
      ...markedWeekDates[endDate],
      startingDay: false,
      endingDay: true,
    }
    markedWeekDates[today] = {
      ...markedWeekDates[today],
      selected: true,
    }
    return markedWeekDates
  }, [darkMode, markedDates, startDate, theme.colors?.white])

  return (
    <Modal
      animationType="slide"
      transparent={true}
      visible={calendarVisible}
      onRequestClose={() => {
        setCalendarVisible(false)
      }}
    >
      <SafeAreaProvider>
        <SafeAreaView style={styles.modalView}>
          <View style={styles.header}>
            <Icon
              name="times"
              type="font-awesome-5"
              onPress={() => setCalendarVisible(false)}
              color={theme.colors?.primary}
              solid
            />
          </View>
          <CalendarList
            hideExtraDays={true}
            markingType="period"
            markedDates={markedCalendar}
            pastScrollRange={5}
            futureScrollRange={5}
            showScrollIndicator={true}
            theme={{
              dotColor: theme.colors?.white,
              todayBackgroundColor: theme.colors?.primary,
              calendarBackground: theme.colors?.white,
              monthTextColor: darkMode ? theme.colors?.grey3 : undefined,
              dayTextColor: darkMode ? theme.colors?.grey3 : undefined,
            }}
            style={{
              width: ScreenWidth,
              height: 50,
            }}
            onDayPress={({ dateString }) => {
              setCalendarVisible(false)
              onCalendarDateSelected(moment(dateString).toDate())
            }}
          />
        </SafeAreaView>
      </SafeAreaProvider>
    </Modal>
  )
}

const useStyles = makeStyles((theme, darkMode: Boolean) => ({
  modalView: {
    flex: 1,
    alignItems: 'center',
  },
  header: {
    width: '100%',
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
    padding: 10,
    paddingRight: 20,
    backgroundColor: darkMode ? '#121212' : '#f9f9f9',
  },
}))
